/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  Formula,
  isFormulaEmpty,
  JeVariationType,
  UomQuantityMapper,
} from '@gh/builder-shared-data';
import { createLayoutComparatorFactory } from '@gh/common-internal-shared';
import {
  createGroupArrayIndexDriver,
  createGroupIndexDriver,
  createOrderIndexDriver,
  createTableDriver,
  isEntityInvalid,
  newEntityComparator,
  Table,
  whenDirty,
  whenExisting,
  whenModified,
  setEntityFields,
} from '@gh/common-shared';
import {
  EntityVersion,
  ID,
  WithDirty,
  WithRecalculationNeeded,
  WithVersion,
} from '@gh/common-shared-data';
import { FormMode, isEditableFormMode } from '@gh/core-ui';
import { FocusedCell, GridLayout } from '@gh/core-ui-grid';
import {
  composeComparators,
  firstByComparator,
  isNotNil,
  orderByComparator,
  sortedIndexWith,
  valueComparator,
  withValueByIndex,
} from '@gh/core-util';
import {
  find,
  findIndex,
  first,
  get,
  isEmpty,
  isNil,
  keyBy,
  maxBy,
  omit,
  overEvery,
  pick,
  sortBy,
  sortedIndexBy,
  sortedUniq,
} from 'lodash';
import {
  getJeComponentId,
  getJeCostCenterId,
  getJeCuttingListItemId,
  getJeItemId,
  getJeOrderId,
  getJeTopId,
  getJeTopRefItemId,
  getJeUomRefItemId,
  hasJeElementUncofirmedVariation,
  isJeCanChangeAuthorization,
  isJeComponentCostCenter,
  isJeElementHasInvalidFormula,
  isJeFreeOrComponentItem,
  isJeHasEditableStatus,
  isJeItemFromComponent,
  isJeOrder0,
  JeCcFilter,
  JeComponent,
  JeCostCenter,
  JeCuttingListItem,
  JeDetails,
  JeDetailsDirtyData,
  JeElement,
  JeElementType,
  JeGetComponentsResponse,
  JeGetCostCenterResponse,
  JeItem,
  JeItemType,
  JeOrder,
  JeStr,
  JeTop,
  JeTopRefItem,
  JeTopViewUsage,
  JeUomRefItem,
} from '../../resources/job-estimate-details.model';
import { JobOrderDetailsMasterInfo } from '../../resources/job-order-details.model';

export enum JeDetailsStateEventType {
  Load, LoadWithCostCenter, MergeOnRecalculate, MergeOnReprice,
  CostCenterAdd, CostCenterSelect, CostCenterLoad, CostCenterSetField, CostCenterSetFilter,
  TopSetField, TopRemove, TopCommit, TopReset,
  ComponentAddSet, ComponentSetField, ComponentRemove,
  CuttingListItemAdd, CuttingListItemSetField, CuttingListItemRemove,
  CostCenterItemAddSet, CostCenterItemSetField, CostCenterItemRemove, CostCenterItemChangeCc,
  OrderAdd, OrderUpdate, OrderRemove,
  SalesTaxRatesSet,
  ComponentSelect, CostCenterItemSelect, DetailsTabSelect,
  UomAddSet, TopAddSet,
  NavigateToInvalid, NavigateToInvalidTop, HighlightInvalid, ResetFocusedCell,
  LockAll, UnlockAll,
  Authorize, Unauthorize,
  LayoutUpdate,
  InitHistory, SaveViewToHistory, AddToHistory, Undo, Redo, CostCenterRemove,
}

export type JeDetailsStateLoadEvent = { type: JeDetailsStateEventType.Load; details: JeDetails; layouts: Hash<GridLayout>; hasEditPermission: boolean };
export type JeDetailsStateLoadWithCostCenterEvent = { type: JeDetailsStateEventType.LoadWithCostCenter; details: JeDetails; costCenterId?: ID; costCenter?: JeGetCostCenterResponse };
export type JeDetailsStateMergeOnRecalculateEvent = { type: JeDetailsStateEventType.MergeOnRecalculate; details: JeDetails };
export type JeDetailsStateMergeOnRepriceEvent = { type: JeDetailsStateEventType.MergeOnReprice; details: JeDetails };

export type JeDetailsStateUomAddSet = { type: JeDetailsStateEventType.UomAddSet; uomSet: JeUomRefItem[] };
export type JeDetailsStateTopAddSet = { type: JeDetailsStateEventType.TopAddSet; topSet: JeTopRefItem[] };
export type JeDetailsStateCostCenterAddEvent = { type: JeDetailsStateEventType.CostCenterAdd; costCenter: JeCostCenter; order: JeOrder };
export type JeDetailsStateCostCenterSetFieldEvent = { type: JeDetailsStateEventType.CostCenterSetField; costCenterId: ID; name: string; value: any };
export type JeDetailsStateCostCenterRemoveEvent = { type: JeDetailsStateEventType.CostCenterRemove; costCenterId: ID };
export type JeDetailsStateCostCenterLoadEvent = { type: JeDetailsStateEventType.CostCenterLoad; costCenterId: ID; costCenter: JeGetCostCenterResponse };
export type JeDetailsStateCostCenterSetFilterEvent = { type: JeDetailsStateEventType.CostCenterSetFilter; filter: JeCcFilter };

export type JeDetailsStateTopRemoveEvent = { type: JeDetailsStateEventType.TopRemove; topId: ID };
export type JeDetailsStateTopSetFieldEvent = { type: JeDetailsStateEventType.TopSetField; topId: ID; name: string; value: any };
export type JeDetailsStateTopCommitEvent = { type: JeDetailsStateEventType.TopCommit; topId: ID };
export type JeDetailsStateTopResetEvent = { type: JeDetailsStateEventType.TopReset; top: JeTop };

export type JeDetailsStateComponentAddSetEvent = { type: JeDetailsStateEventType.ComponentAddSet; componentSet: JeGetComponentsResponse };
export type JeDetailsStateComponentSetFieldEvent = { type: JeDetailsStateEventType.ComponentSetField; componentId: ID; name: string; value: any };
export type JeDetailsStateComponentRemoveEvent = { type: JeDetailsStateEventType.ComponentRemove; componentId: ID };

export type JeDetailsStateCostCenterItemAddSetEvent = { type: JeDetailsStateEventType.CostCenterItemAddSet; costCenterId: ID; itemSet: JeItem[] };
export type JeDetailsStateCostCenterItemSetFieldEvent = { type: JeDetailsStateEventType.CostCenterItemSetField; itemId: ID; name: string; value: any };
export type JeDetailsStateCostCenterItemRemoveEvent = { type: JeDetailsStateEventType.CostCenterItemRemove; itemId: ID };
export type JeDetailsStateCostCenterItemChangeCcEvent = { type: JeDetailsStateEventType.CostCenterItemChangeCc; itemId: ID; costCenterId: ID };

export type JeDetailsStateCostCenterSelectEvent = { type: JeDetailsStateEventType.CostCenterSelect; costCenterId?: ID };
export type JeDetailsStateComponentSelectEvent = { type: JeDetailsStateEventType.ComponentSelect; componentId?: ID };
export type JeDetailsStateItemSelectEvent = { type: JeDetailsStateEventType.CostCenterItemSelect; itemId?: ID };
export type JeDetailsStateDetailsTabSelectEvent = { type: JeDetailsStateEventType.DetailsTabSelect; name: string };

export type JeDetailsStateNavigateToInvalidEvent = { type: JeDetailsStateEventType.NavigateToInvalid };
export type JeDetailsStateNavigateToInvalidTopEvent = { type: JeDetailsStateEventType.NavigateToInvalidTop; topId: ID };
export type JeDetailsStateHighlightInvalidEvent = { type: JeDetailsStateEventType.HighlightInvalid };
export type JeDetailsStateResetFocusedCellEvent = { type: JeDetailsStateEventType.ResetFocusedCell };

export type JeDetailsStateLockAllEvent = { type: JeDetailsStateEventType.LockAll };
export type JeDetailsStateUnlockAllEvent = { type: JeDetailsStateEventType.UnlockAll };

export type JeDetailsStateOrderAddEvent = { type: JeDetailsStateEventType.OrderAdd; order: JeOrder };
export type JeDetailsStateOrderUpdateEvent = { type: JeDetailsStateEventType.OrderUpdate; order: JeOrder };
export type JeDetailsStateOrderRemoveEvent = { type: JeDetailsStateEventType.OrderRemove; orderId: ID };

export type JeDetailsStateSalesTaxRatesSetEvent = { type: JeDetailsStateEventType.SalesTaxRatesSet; rates: JeStr[] };

export type JeDetailsStateAuthorizeEvent = { type: JeDetailsStateEventType.Authorize; version: EntityVersion };
export type JeDetailsStateUnauthorizeEvent = { type: JeDetailsStateEventType.Unauthorize; version: EntityVersion };
export type JeDetailsStateLayoutUpdateEvent = { type: JeDetailsStateEventType.LayoutUpdate; layout: GridLayout };

export type JeDetailsStateInitHistoryEvent = { type: JeDetailsStateEventType.InitHistory };
export type JeDetailsStateSaveViewToHistoryEvent = { type: JeDetailsStateEventType.SaveViewToHistory };
export type JeDetailsStateAddToHistoryEvent = { type: JeDetailsStateEventType.AddToHistory };
export type JeDetailsStateUndoEvent = { type: JeDetailsStateEventType.Undo };
export type JeDetailsStateRedoEvent = { type: JeDetailsStateEventType.Redo };

export type JeDetailsStateCuttingListItemAddEvent = {
  type: JeDetailsStateEventType.CuttingListItemAdd;
  sourceType: JeElementType;
  sourceId: ID;
  item: JeCuttingListItem;
};

export type JeDetailsStateCuttingListItemRemoveEvent = {
  type: JeDetailsStateEventType.CuttingListItemRemove;
  sourceType: JeElementType;
  sourceId: ID;
  cuttingId: ID;
};

export type JeDetailsStateCuttingListItemSetFieldEvent = {
  type: JeDetailsStateEventType.CuttingListItemSetField;
  sourceType: JeElementType;
  sourceId: ID;
  cuttingId: ID;
  name: string;
  value: any;
};

export enum JeComponentDetailsTab {
  Items = 'ITEMS',
  CuttingList = 'CUTTING_LIST',
  Formula = 'FORMULA',
  Memo = 'MEMO',
}

export enum JeItemDetailsTab {
  Component = 'COMPONENT',
  CuttingList = 'CUTTING_LIST',
  Formula = 'FORMULA',
  Memo = 'MEMO',
}

export type JeDetailsStateEvent =
  JeDetailsStateLoadEvent
  | JeDetailsStateLoadWithCostCenterEvent
  | JeDetailsStateMergeOnRecalculateEvent
  | JeDetailsStateMergeOnRepriceEvent
  | JeDetailsStateUomAddSet
  | JeDetailsStateTopAddSet
  | JeDetailsStateCostCenterAddEvent
  | JeDetailsStateCostCenterLoadEvent
  | JeDetailsStateCostCenterSetFilterEvent
  | JeDetailsStateCostCenterSetFieldEvent
  | JeDetailsStateTopRemoveEvent
  | JeDetailsStateTopSetFieldEvent
  | JeDetailsStateTopCommitEvent
  | JeDetailsStateTopResetEvent
  | JeDetailsStateComponentAddSetEvent
  | JeDetailsStateCostCenterRemoveEvent
  | JeDetailsStateComponentSetFieldEvent
  | JeDetailsStateComponentRemoveEvent
  | JeDetailsStateCuttingListItemAddEvent
  | JeDetailsStateCuttingListItemRemoveEvent
  | JeDetailsStateCuttingListItemSetFieldEvent
  | JeDetailsStateCostCenterItemAddSetEvent
  | JeDetailsStateCostCenterItemSetFieldEvent
  | JeDetailsStateCostCenterItemRemoveEvent
  | JeDetailsStateCostCenterItemChangeCcEvent
  | JeDetailsStateOrderAddEvent
  | JeDetailsStateOrderUpdateEvent
  | JeDetailsStateOrderRemoveEvent
  | JeDetailsStateSalesTaxRatesSetEvent
  | JeDetailsStateCostCenterSelectEvent
  | JeDetailsStateComponentSelectEvent
  | JeDetailsStateItemSelectEvent
  | JeDetailsStateDetailsTabSelectEvent
  | JeDetailsStateNavigateToInvalidEvent
  | JeDetailsStateNavigateToInvalidTopEvent
  | JeDetailsStateHighlightInvalidEvent
  | JeDetailsStateResetFocusedCellEvent
  | JeDetailsStateLockAllEvent
  | JeDetailsStateUnlockAllEvent
  | JeDetailsStateAuthorizeEvent
  | JeDetailsStateUnauthorizeEvent
  | JeDetailsStateLayoutUpdateEvent
  | JeDetailsStateInitHistoryEvent
  | JeDetailsStateSaveViewToHistoryEvent
  | JeDetailsStateAddToHistoryEvent
  | JeDetailsStateUndoEvent
  | JeDetailsStateRedoEvent;

export enum JeFocusedTable { TopList, ComponentList, ItemList, CuttingList }

export type JeDetailsSelection = {
  costCenterId?: ID;
  componentId?: ID;
  itemId?: ID;
  detailsTabName?: string;
  focusedTable?: JeFocusedTable;
  focusedCell?: FocusedCell;
  highlightInvalidFormula?: boolean;
};

export type JeDetailsMasterInfo = {
  jobNumber: string;
  description: string;
  siteAddress: string;
  customerName: string;
  contactId: ID;
  cost: number;
  contractAmount: number;
  variationsAmount: number;
  isDpp: boolean;
  isAuthorized: boolean;
  isFinalized: boolean;
  isNotProceeding: boolean;
  jobDocsFolderId: ID;
  jobDocsJobPlansFolderId: ID;
};

export type JeDetailsState = WithVersion<WithRecalculationNeeded<WithDirty<{
  jobId: ID;
  masterInfo: JeDetailsMasterInfo;
  jobComponentsCcId: ID;
  uoms: Table<JeUomRefItem>;
  tops: Table<JeTopRefItem>;
  jobTops: Table<JeTop>;
  jobCostCenters: Table<JeCostCenter>;
  jobComponents: Table<JeComponent>;
  jobItems: Table<JeItem>;
  jobOrders: Table<JeOrder>;
  salesTaxRates: JeStr[];
  selection: JeDetailsSelection;
  isProcessChangesAsap: boolean;
  allowSupplierChange: boolean;
  ccFilter: JeCcFilter;
  hasEditPermission: boolean;
  layouts: Hash<GridLayout>;
  memo?: string;
  history?: JeDetailsState[];
  historyPosition: number;
  historyLength: number;
}>>>;

export type JeElementInfo = {
  type: JeElementType;
  id: ID;
};

export const JE_SALES_TAX_DIVIDER: JeItem = {
  costCenterId: '',
  jobItemId: '_DIVIDER',
  itemId: '',
  code: '',
  description: '',
  isSalesTax: false,
  quantity: 0,
  cost: 0,
  uomId: '',
  extension: 0,
  extensionComponent: 0,
  isFormulaNeedPropagation: false,
  isLocked: false,
  variationType: JeVariationType.None,
  type: JeItemType.Divider,
};

export const jeTopComparator = valueComparator<JeTop>((top) => top.code.toLowerCase());
export const jeComponentComparator = composeComparators<JeComponent>(
  newEntityComparator,
  valueComparator('code'),
  valueComparator(getJeComponentId));
export const jeItemDefaultComparator = composeComparators<JeItem>(
  newEntityComparator,
  valueComparator('code'),
  valueComparator(({ order }) => order && order.orderNumber),
  valueComparator(getJeItemId));
export const jeCcComparator = composeComparators<JeCostCenter>(
  valueComparator('code'),
  valueComparator(getJeCostCenterId));
export const jeOrderComparator = composeComparators<JeOrder>(
  valueComparator('orderNumber'),
  valueComparator(getJeOrderId),
);
export const jeCuttingListItemComparator = composeComparators<JeCuttingListItem>(
  newEntityComparator,
  valueComparator('sortOrder'),
  valueComparator(getJeCuttingListItemId));

export const jeComponentLayoutComparatorFactory = createLayoutComparatorFactory(
  jeComponentComparator,
  valueComparator(getJeComponentId));
export const jeItemLayoutComparatorFactory = createLayoutComparatorFactory(
  jeItemDefaultComparator,
  valueComparator(getJeItemId));

export const jeUomRefTable = createTableDriver(getJeUomRefItemId);
export const jeTopRefTable = createTableDriver(getJeTopRefItemId);
export const jeTopTable = createTableDriver(
  getJeTopId,
  createOrderIndexDriver('code', getJeTopId, whenExisting, jeTopComparator),
  createOrderIndexDriver(
    'errors',
    getJeTopId,
    overEvery([isEntityInvalid, whenExisting]),
    jeTopComparator));
export const jeCcTable = createTableDriver(
  getJeCostCenterId,
  createOrderIndexDriver('code', getJeCostCenterId, whenExisting, jeCcComparator));
export const jeComponentTable = createTableDriver(
  getJeComponentId,
  createOrderIndexDriver('code', getJeComponentId, whenExisting, jeComponentComparator),
  createOrderIndexDriver(
    'errors',
    getJeComponentId,
    overEvery([isEntityInvalid, whenExisting]),
    jeComponentComparator),
  createOrderIndexDriver<JeComponent>(
    'unconfirmedVars',
    getJeComponentId,
    overEvery([whenExisting, hasJeElementUncofirmedVariation]),
    jeComponentComparator),
  createGroupArrayIndexDriver(
    'topId',
    getJeComponentId,
    whenExisting,
    (component) => component.topIds,
    valueComparator(getJeComponentId)));
export const jeItemTable = createTableDriver(
  getJeItemId,
  createOrderIndexDriver<JeItem>(
    'unconfirmedVars',
    getJeItemId,
    overEvery([whenExisting, hasJeElementUncofirmedVariation]),
    jeItemDefaultComparator),
  createGroupIndexDriver(
    'component',
    getJeItemId,
    whenExisting,
    (item) => item.jobComponentId,
    valueComparator<JeItem>(getJeItemId)),
  createGroupIndexDriver(
    'costCenter',
    getJeItemId,
    whenExisting,
    (item) => item.costCenterId,
    jeItemDefaultComparator),
  createGroupIndexDriver(
    'costCenterErrors',
    getJeItemId,
    overEvery([isEntityInvalid, whenExisting]),
    (item) => item.costCenterId,
    jeItemDefaultComparator),
  createGroupIndexDriver(
    'order',
    getJeItemId,
    whenExisting,
    ({ order }) => order && order.orderId,
    valueComparator<JeItem>(getJeItemId)),
  createGroupArrayIndexDriver(
    'topId',
    getJeItemId,
    whenExisting,
    (item) => item.topIds,
    valueComparator<JeItem>(getJeItemId)));
export const jeOrderTable = createTableDriver(
  getJeOrderId,
  createOrderIndexDriver('dirty', getJeOrderId, whenModified, valueComparator(getJeOrderId)),
  createGroupIndexDriver(
    'costCenter',
    getJeOrderId,
    whenExisting,
    (order) => order.costCenterId,
    jeOrderComparator));

export const JE_DETAILS_INIT_STATE: JeDetailsState = {
  hasEditPermission: false,
  jobId: '',
  masterInfo: <JeDetailsMasterInfo>{},
  jobComponentsCcId: '',
  uoms: jeUomRefTable.create(),
  tops: jeTopRefTable.create(),
  jobTops: jeTopTable.create(),
  jobCostCenters: jeCcTable.create(),
  jobComponents: jeComponentTable.create(),
  jobItems: jeItemTable.create(),
  jobOrders: jeOrderTable.create(),
  salesTaxRates: [],
  ccFilter: JeCcFilter.ShowAll,
  selection: {},
  layouts: {},
  isDirty: false,
  isRecalculationNeeded: false,
  isProcessChangesAsap: false,
  allowSupplierChange: false,
  history: [],
  historyPosition: 0,
  historyLength: 12,
};

export const queryJeJobId = (state: JeDetailsState) => state.jobId;
export const queryJeComponents = (state: JeDetailsState) => jeComponentTable.byOrder(state.jobComponents, 'code');
export const queryJeItems = (state: JeDetailsState) => jeItemTable.all(state.jobItems);
export const queryJeOrderItems = (state: JeDetailsState, id: ID) => jeItemTable.byGroup(state.jobItems, 'order', id);
export const queryJeDirtyComponents = (state: JeDetailsState) => jeComponentTable.all(state.jobComponents)
  .filter(whenDirty);
export const queryJeComponent = (state: JeDetailsState, componentId: ID) =>
  jeComponentTable.byId(state.jobComponents, componentId);
export const queryJeFirstComponent = (state: JeDetailsState) =>
  queryJeFirstComponentAmong(state, jeComponentTable.byOrder(state.jobComponents, 'code'));
export const queryJeFirstComponentAmong = (state: JeDetailsState, components: JeComponent[]) =>
  firstByComparator(components, jeComponentLayoutComparatorFactory(queryJeComponentLayout(state)));
export const queryJeComponentItems = (state: JeDetailsState, componentId: ID) =>
  jeItemTable.byGroup(state.jobItems, 'component', componentId);
export const queryJeCostCenterItems = (state: JeDetailsState, costCenterId: ID) =>
  jeItemTable.byGroup(state.jobItems, 'costCenter', costCenterId);
export const queryJeFirstCostCenterItem = (state: JeDetailsState, ccId: ID) =>
  queryJeFirstCostCenterItemAmong(state, jeItemTable.byGroup(state.jobItems, 'costCenter', ccId));
export const queryJeFirstCostCenterItemAmong = (state: JeDetailsState, items: JeItem[]) =>
  firstByComparator(items, jeItemLayoutComparatorFactory(queryJeItemLayout(state)));
export const queryJeDirtyCostCenters = (state: JeDetailsState) =>
  jeCcTable.all(state.jobCostCenters).filter(whenDirty);
export const queryJeDirtyItems = (state: JeDetailsState) => jeItemTable.all(state.jobItems).filter(whenDirty);
export const queryJeAllTops = (state: JeDetailsState) => jeTopTable.all(state.jobTops);
export const queryJeDirtyCostCenterItems = (state: JeDetailsState, costCenterId: ID) =>
  jeItemTable.all(state.jobItems).filter((item) => item.costCenterId === costCenterId && whenDirty(item));
export const queryJeItem = (state: JeDetailsState, itemId: ID) => jeItemTable.byId(state.jobItems, itemId);
export const queryJeCostCenters = (state: JeDetailsState) => jeCcTable.byOrder(state.jobCostCenters, 'code');
export const queryJeCostCentersByName = (state: JeDetailsState, name: string) => {
  const normalizedName = name && name.toLowerCase();
  const costCenters = jeCcTable.byOrder(state.jobCostCenters, 'code')
    .filter((cc) => !isJeComponentCostCenter(cc));
  return normalizedName ? costCenters.filter((cc) =>
    cc.name.toLowerCase().includes(normalizedName) || cc.code.toString().includes(normalizedName)) : costCenters;
};

export const queryJeHistory = (state: JeDetailsState) => state.history ? state.history[state.historyPosition] : undefined;
export const queryJeHistoryPosition = (state: JeDetailsState) => state.historyPosition;
export const queryJeUndoDisabled = (state: JeDetailsState) => !state.history || !state.history.length ||
  state.history.length === 1 ||
  state.historyPosition === 0;
export const queryJeRedoDisabled = (state: JeDetailsState) =>  !state.history || !state.history.length ||
  state.historyPosition === state.history.length - 1;

export const queryJeCostCenter = (state: JeDetailsState, id: ID) => jeCcTable.byId(state.jobCostCenters, id);
export const queryJeCcc = (state: JeDetailsState) =>
  <JeCostCenter>jeCcTable.byId(state.jobCostCenters, state.jobComponentsCcId);
export const queryJeCccId = (state: JeDetailsState) => state.jobComponentsCcId;
export const queryJeIsCccId = (state: JeDetailsState, id: ID) => state.jobComponentsCcId === id;
export const queryJeCcFilter = (state: JeDetailsState) => state.ccFilter;

export const queryJeTops = (state: JeDetailsState) => jeTopTable.byOrder(state.jobTops, 'code');

export const queryJeTopRefItems = (state: JeDetailsState) => jeTopRefTable.all(state.tops);
export const queryJeTopRefItem = (state: JeDetailsState, id: ID) => jeTopRefTable.byId(state.tops, id);

export const queryJeUomRefItem = (state: JeDetailsState, id: ID) => jeUomRefTable.byId(state.uoms, id);

export const queryJeTop = (state: JeDetailsState, id: ID) => jeTopTable.byId(state.jobTops, id);

export const queryJeFormMode = (state: JeDetailsState) =>
  state.hasEditPermission && isJeHasEditableStatus(state.masterInfo) ? FormMode.EditMode : FormMode.ViewMode;
export const queryJeEditable = (state: JeDetailsState) => isEditableFormMode(queryJeFormMode(state));
export const queryJeAuthorized = (state: JeDetailsState) => state.masterInfo.isAuthorized;
export const queryJeCanChangeAuthorization = (state: JeDetailsState) => isJeCanChangeAuthorization(state.masterInfo);

export const queryJeProcessChangesAsap = (state: JeDetailsState) => state.isProcessChangesAsap;

export const queryJeElement = (state: JeDetailsState,
                               sourceType: JeElementType,
                               sourceId: ID): JeElement => {
  if (sourceType === JeElementType.Component) {
    return <JeComponent>jeComponentTable.byId(state.jobComponents, sourceId);
  } else {
    return <JeItem>jeItemTable.byId(state.jobItems, sourceId);
  }
};

export const queryJeCuttingList = (state: JeDetailsState,
                                   sourceType: JeElementType,
                                   sourceId: ID): JeCuttingListItem[] =>
  get(queryJeElement(state, <any>sourceType, sourceId), 'cuttingList') || [];

export const queryJeSelectedElementInfo = (state: JeDetailsState): Maybe<JeElementInfo> => {
  const { componentId, itemId } = state.selection;

  if (componentId) {
    return { type: JeElementType.Component, id: componentId };
  } else if (itemId) {
    return { type: JeElementType.Item, id: itemId };
  } else {
    return void 0;
  }
};

export const queryJeSelectedCostCenter = (state: JeDetailsState): Maybe<JeCostCenter> => {
  const { selection: { costCenterId } } = state;
  if (costCenterId) {
    return jeCcTable.byId(state.jobCostCenters, costCenterId);
  } else {
    return void 0;
  }
};

export const queryJeSelectedCostCenterId = (state: JeDetailsState) => state.selection.costCenterId;

export const queryJeIsCccSelected = (state: JeDetailsState) => {
  const ccId = queryJeSelectedCostCenterId(state);
  return ccId ? queryJeIsCccId(state, ccId) : false;
};

export const queryJeSelectedComponent = (state: JeDetailsState): Maybe<JeComponent> => {
  const { selection: { componentId } } = state;
  if (componentId) {
    return jeComponentTable.byId(state.jobComponents, componentId);
  } else {
    return void 0;
  }
};

export const queryJeSelectedComponentId = (state: JeDetailsState) => state.selection.componentId;

export const queryJeSelectedItemId = (state: JeDetailsState) => state.selection.itemId;

export const queryJeSelectedItem = (state: JeDetailsState): Maybe<JeItem> => {
  const { selection: { itemId } } = state;
  if (itemId) {
    return jeItemTable.byId(state.jobItems, itemId);
  } else {
    return void 0;
  }
};

export const queryJeSelectedDetailsTab = (state: JeDetailsState) => state.selection.detailsTabName;

export const queryJeMasterInfo = (state: JeDetailsState) => state.masterInfo;

export const ensureJeSalesTaxDivider = (items: JeItem[]): JeItem[] => {
  const strIndex = findIndex(items, { type: JeItemType.SalesTax });
  if (strIndex >= 0) {
    return withValueByIndex(items, strIndex, JE_SALES_TAX_DIVIDER);
  } else {
    return items;
  }
};

export const queryJeHasErrors = (state: JeDetailsState) => {
  if (!isEmpty(jeTopTable.byOrder(state.jobTops, 'errors'))) {
    return true;
  }
  if (!isEmpty(jeComponentTable.byOrder(state.jobComponents, 'errors'))) {
    return true;
  }
  return jeCcTable.all(state.jobCostCenters).some((cc) => !isEmpty(jeItemTable.byGroup(
    state.jobItems,
    'costCenterErrors',
    getJeCostCenterId(cc))));
};
export const queryJeComponentUnconfirmedVars = (state: JeDetailsState) =>
  jeComponentTable.byOrder(state.jobComponents, 'unconfirmedVars');
export const queryJeItemUnconfirmedVars = (state: JeDetailsState) =>
  jeItemTable.byOrder(state.jobItems, 'unconfirmedVars');
export const queryJeUnconfirmedVarCount = (state: JeDetailsState) =>
  queryJeComponentUnconfirmedVars(state).length + queryJeItemUnconfirmedVars(state).length;
export const queryJeOrders = (state: JeDetailsState) => jeOrderTable.all(state.jobOrders);
export const queryJeOrder = (state: JeDetailsState, id: ID) => jeOrderTable.byId(state.jobOrders, id);
export const queryJeCostCenterOrders = (state: JeDetailsState, costCenterId: ID) =>
  jeOrderTable.byGroup(state.jobOrders, 'costCenter', costCenterId);
export const queryJeDirtyOrders = (state: JeDetailsState) => jeOrderTable.byOrder(state.jobOrders, 'dirty');

export const queryJeSalesTaxRates = (state: JeDetailsState) => state.salesTaxRates;

export const queryJeDirtyData = (state: JeDetailsState): JeDetailsDirtyData => ({
  jobCostCenters: queryJeDirtyCostCenters(state),
  jobComponents: queryJeDirtyComponents(state),
  jobItems: queryJeDirtyItems(state).filter(isJeFreeOrComponentItem),
  jobOrders: queryJeDirtyOrders(state),
  jobTops: queryJeAllTops(state),
  salesTaxRates: queryJeSalesTaxRates(state),
  memo: state.memo,
});

export const queryJeTopViewUsage = (state: JeDetailsState, topId: ID): JeTopViewUsage => {
  const top = queryJeTop(state, topId);
  const ccc = queryJeCcc(state);

  const components = queryJeComponentsByTopId(state, topId)
    .map((component) => ({
      costCenterId: getJeCostCenterId(ccc),
      costCenterCode: ccc.code,
      recordId: getJeComponentId(component),
      recordCode: component.code,
    }));

  const costCenters = keyBy(queryJeCostCenters(state), getJeCostCenterId);

  const items = queryJeItemsByTopId(state, topId)
    .map((item) => ({
      costCenterId: item.costCenterId,
      costCenterCode: costCenters[item.costCenterId].code,
      recordId: getJeItemId(item),
      recordCode: item.code,
    }));

  return {
    jobNumber: state.masterInfo.jobNumber,
    siteAddress: state.masterInfo.siteAddress,
    topName: top && top.code || '',
    records: components.concat(sortBy(items, 'costCenterCode')),
  };
};

export const queryJeCcHasErrors = (state: JeDetailsState, ccId: ID) =>
  jeItemTable.byGroup(state.jobItems, 'costCenterErrors', ccId).length > 0;

export const queryJeFirstInvalidItem = (state: JeDetailsState, ccId: ID) =>
  firstByComparator(
    jeItemTable.byGroup(state.jobItems, 'costCenterErrors', ccId),
    jeItemLayoutComparatorFactory(queryJeItemLayout(state)));
export const queryJeFirstInvalidComponent = (state: JeDetailsState) =>
  firstByComparator(
    jeComponentTable.byOrder(state.jobComponents, 'errors'),
    jeComponentLayoutComparatorFactory(queryJeComponentLayout(state)));
export const queryJeFirstInvalidTop = (state: JeDetailsState) =>
  first(jeTopTable.byOrder(state.jobTops, 'errors'));

export const queryJeFocusedCell = ({ selection: { focusedTable, focusedCell } }: JeDetailsState,
                                   table: JeFocusedTable) => focusedTable === table ? focusedCell : void 0;

export const queryJeComponentsByTopId = (state: JeDetailsState, topId: ID) =>
  jeComponentTable.byGroup(state.jobComponents, 'topId', topId);

export const queryJeItemsByTopId = (state: JeDetailsState, topId: ID) =>
  jeItemTable.byGroup(state.jobItems, 'topId', topId);

export const queryJeIsSelectionEmpty = (state: JeDetailsState) => isEmpty(state.selection);

export const queryJeSelectedElement = (state: JeDetailsState) => {
  const elementInfo = queryJeSelectedElementInfo(state);
  return isNil(elementInfo) ? void 0 : queryJeElement(state, elementInfo.type, elementInfo.id);
};

/**
 * Undo/Redo functionality
 */
export function initHistory(state: JeDetailsState): JeDetailsState {
  const newHistory = {
    ...omit(state, 'history')
  } as JeDetailsState;
  return setEntityFields(state, {
    historyPosition: 0,
    history: [newHistory],
  });
}

export function saveViewToHistory(state: JeDetailsState): JeDetailsState {
  const history = state.history ? [...state.history] : [];
  const historyPosition = state.historyPosition;
  const currentHistory = {
    ...omit(state, 'history'),
  } as JeDetailsState;
  history[historyPosition] = currentHistory;
  return setEntityFields(state, {
    history,
  });
}

export function addToHistory(state: JeDetailsState): JeDetailsState {
  const history = state.history ? state.history.slice(0, state.historyPosition + 1) : [];
  const newHistory = {
    ...omit(state, 'history'),
  } as JeDetailsState;
  if (history.length <= state.historyLength) {
    history.push(newHistory);
  } else {
    history.shift();
    history.push(newHistory);
  }
  const historyPosition = history.length - 1;
  return setEntityFields(state, {
    historyPosition,
    history,
  });
}

export function undo(state: JeDetailsState): JeDetailsState {
  const history = state.history ? [...state.history] : [];
  const historyPosition = state.historyPosition - 1;
  const undoState = history[historyPosition];
  return setEntityFields({...undoState}, {
    historyPosition,
    history,
    isDirty: undoState.isDirty,
  });
}

export function redo(state: JeDetailsState): JeDetailsState {
  const history = state.history ? [...state.history] : [];
  const historyPosition = state.historyPosition + 1;
  const redoState = history[historyPosition];
  return setEntityFields({...redoState}, {
    historyPosition,
    history,
    isDirty: redoState.isDirty,
  });
}

/**
 * Validates if currently selected element has error in the formula
 */
export const queryJeElementHasErrors = (state: JeDetailsState) => {
  const element = queryJeSelectedElement(state);

  return element && isJeElementHasInvalidFormula(element);
};

/**
 * Answers whether JE has at least one ToP measured in GTO.
 */
export const queryJeHasTopsFromGto = (state: JeDetailsState) => queryJeTops(state).some((top) => top.isFromGto);

export const queryJeSelectionHighlightInvalid = ({ selection: { highlightInvalidFormula } }: JeDetailsState) =>
  highlightInvalidFormula || false;

export const queryJeComponentHavingUnconfirmedVarAfter = (state: JeDetailsState,
                                                          componentId?: ID): Maybe<JeComponent> => {
  const comparator = jeComponentLayoutComparatorFactory(queryJeComponentLayout(state));
  const unconfirmedVars = orderByComparator(queryJeComponentUnconfirmedVars(state), comparator);
  if (componentId) {
    const component = componentId ? queryJeComponent(state, componentId) : first(unconfirmedVars);
    const currentPosition = component ? sortedIndexWith(unconfirmedVars, component, comparator) : -1;
    const nextPosition = currentPosition >= 0 && unconfirmedVars[currentPosition] === component
      ? currentPosition + 1
      : currentPosition;
    return nextPosition < 0 || nextPosition === unconfirmedVars.length ? void 0 : unconfirmedVars[nextPosition];
  } else {
    return first(unconfirmedVars);
  }
};

export const queryJeItemHavingUnconfirmedVarInCcAfter = (state: JeDetailsState,
                                                         costCenterId: ID,
                                                         itemId?: ID): Maybe<JeItem> => {
  const comparator = jeItemLayoutComparatorFactory(queryJeItemLayout(state));
  const unconfirmedVars = orderByComparator(queryJeItemUnconfirmedVars(state), comparator);
  const unconfirmedVarsForCc = unconfirmedVars.filter((itemVar) => itemVar.costCenterId === costCenterId);

  const item = itemId ? queryJeItem(state, itemId) : void 0;
  if (item && item.costCenterId === costCenterId) {
    const currentPosition = item ? sortedIndexWith(unconfirmedVarsForCc, item, comparator) : -1;
    const nextPosition = currentPosition >= 0 && unconfirmedVarsForCc[currentPosition] === item
      ? currentPosition + 1
      : currentPosition;
    return nextPosition < 0 || nextPosition === unconfirmedVarsForCc.length
      ? void 0 :
      unconfirmedVarsForCc[nextPosition];
  } else {
    return first(unconfirmedVarsForCc);
  }
};

export const queryJeCostCenterHavingUnconfirmedVarAfter = (state: JeDetailsState,
                                                           costCenterId?: ID): Maybe<JeCostCenter> => {
  const costCenter = costCenterId ? queryJeCostCenter(state, costCenterId) : void 0;
  const ccHavingUnconfirmedVars = sortedUniq(orderByComparator(
    queryJeItemUnconfirmedVars(state).map((item) => queryJeCostCenter(state, item.costCenterId)),
    jeCcComparator));

  if (costCenter) {
    const currentPosition = sortedIndexWith(ccHavingUnconfirmedVars, costCenter, jeCcComparator);
    const nextPosition = currentPosition >= 0 && ccHavingUnconfirmedVars[currentPosition] === costCenter
      ? currentPosition + 1
      : currentPosition;
    return nextPosition < 0 || nextPosition === ccHavingUnconfirmedVars.length
      ? void 0 :
      ccHavingUnconfirmedVars[nextPosition];
  } else {
    return first(ccHavingUnconfirmedVars);
  }
};

export const queryJeItemHavingUnconfirmedVarAfter = (state: JeDetailsState,
                                                     costCenterId?: ID,
                                                     itemId?: ID): Maybe<JeItem> => {
  const selectedCcItem = costCenterId ? queryJeItemHavingUnconfirmedVarInCcAfter(state, costCenterId, itemId) : void 0;
  if (selectedCcItem) {
    return selectedCcItem;
  }

  const nextCc = queryJeCostCenterHavingUnconfirmedVarAfter(state, costCenterId);
  return nextCc ? queryJeItemHavingUnconfirmedVarInCcAfter(state, getJeCostCenterId(nextCc), itemId) : void 0;
};

export const queryJeFirstComponentHavingUnconfirmedVar = queryJeComponentHavingUnconfirmedVarAfter;

export const queryJeNextComponentHavingUnconfirmedVar = (state: JeDetailsState): Maybe<JeComponent> =>
  queryJeComponentHavingUnconfirmedVarAfter(state, queryJeSelectedComponentId(state));

export const queryJeFirstItemHavingUnconfirmedVar = queryJeItemHavingUnconfirmedVarAfter;

export const queryJeNextItemHavingUnconfirmedVar = (state: JeDetailsState): Maybe<JeItem> =>
  queryJeItemHavingUnconfirmedVarAfter(
    state,
    queryJeSelectedCostCenterId(state),
    queryJeSelectedItemId(state));

export const buildJeUnconfirmedVarNavigateFlow = (state: JeDetailsState) =>
  queryJeIsCccSelected(state)
    ? [
      queryJeNextComponentHavingUnconfirmedVar,
      queryJeFirstItemHavingUnconfirmedVar,
      queryJeFirstComponentHavingUnconfirmedVar]
    : [
      queryJeNextItemHavingUnconfirmedVar,
      queryJeFirstComponentHavingUnconfirmedVar,
      queryJeFirstItemHavingUnconfirmedVar];

export const queryJeNextUnconfirmedVariation = (state: JeDetailsState): Maybe<JeElement> =>
  buildJeUnconfirmedVarNavigateFlow(state).reduce(
    (lastElement, navigateRule) => lastElement || navigateRule(state),
    <Maybe<JeElement>>void 0);

export const queryJeNextOrderNumber = (state: JeDetailsState, costCenterId: ID) => {
  const maxOrder = maxBy(queryJeCostCenterOrders(state, costCenterId), 'orderNumber');
  return maxOrder ? maxOrder.orderNumber + 1 : 0;
};

export const queryJeAllModifiedCostCenters = (oldState: JeDetailsState, newState: JeDetailsState): JeCostCenter[] =>
  queryJeCostCenters(oldState).filter((cc) => {
    const ccId = getJeCostCenterId(cc);
    if (queryJeIsCccId(oldState, ccId)) {
      return queryJeComponents(oldState) !== queryJeComponents(newState);
    } else {
      return queryJeCostCenterItems(oldState, ccId) !== queryJeCostCenterItems(newState, ccId);
    }
  });

export const queryJeAllModifiedComponents = (oldState: JeDetailsState, newState: JeDetailsState): JeComponent[] =>
  queryJeComponents(oldState).filter((component) => {
    const componentId = getJeComponentId(component);
    return queryJeComponentItems(oldState, componentId) !== queryJeComponentItems(newState, componentId);
  });

export const queryJeCostCenterHasDirtyChanges = (state: JeDetailsState, costCenterId: ID) =>
  queryJeCostCenterItems(state, costCenterId).some(whenModified);

export const queryJeHasCostCenter = (state: JeDetailsState, costCenterId: ID) =>
  isNotNil(queryJeCostCenter(state, costCenterId));

export const queryJeCostCenterOrder0 = (state: JeDetailsState, costCenterId: ID) =>
  find(queryJeCostCenterOrders(state, costCenterId), isJeOrder0);

export const queryJeItemLayout = (state: JeDetailsState): GridLayout => state.layouts['jeItems'];
export const queryJeComponentLayout = (state: JeDetailsState): GridLayout => state.layouts['jeComponents'];

export const queryJeOrderDetailsMasterInfo = (state: JeDetailsState): JobOrderDetailsMasterInfo => ({
// @ts-expect-error Legacy typing suppression
  jobId: queryJeJobId(state),
  ...<JobOrderDetailsMasterInfo>pick(
    queryJeMasterInfo(state),
    ['isDpp', 'isAuthorized', 'isFinalized', 'isNotProceeding', 'jobDocsFolderId']),
});

export const queryJeCostCenterByCode = (state: JeDetailsState, code: number) => {
  const costCenters = queryJeCostCenters(state);
// @ts-expect-error Legacy typing suppression
  const targetCcIndex = sortedIndexBy(costCenters, { code }, 'code');
  const cc = costCenters[targetCcIndex];
  return cc && cc.code === code ? cc : void 0;
};

export const queryJeCostCenterOrderByNumber = (state: JeDetailsState, costCenterId: ID, orderNumber: number) => {
  const orders = queryJeCostCenterOrders(state, costCenterId);
  return find(orders, { orderNumber });
};

export const queryJeElementFormulaCalculation = (state: JeDetailsState, element: JeElement) => {
  const { formula, uomId } = element;
  if (isNil(formula) || isFormulaEmpty(formula)) {
    return;
  }
  const tops = formula.takeOffParams.map((topId) => queryJeTop(state, topId)!);
  return {
    formula,
    uomId,
    takeOffParameters: tops.map(({ topId, value }) => ({ id: topId, value: UomQuantityMapper.serialize(value) })),
    isApproximated: false,
  };
};

export const queryJeFormulaFirstInvalidTop = (state: JeDetailsState, formula: Formula) =>
  find(formula.takeOffParams.map((topId) => queryJeTop(state, topId)), (top) => top ? isEntityInvalid(top) : false);

export const queryJeAllowSupplierChange = ({ allowSupplierChange }: JeDetailsState) => allowSupplierChange;

export const getJeBestComponentDetailsTab = (component?: JeComponent) => {
  if (isNil(component)) {
    return void 0;
  }

  return JeComponentDetailsTab.Items;
};

export const queryJeBestComponentDetailsTab = (state: JeDetailsState, componentId?: ID) =>
  getJeBestComponentDetailsTab(componentId ? queryJeComponent(state, componentId) : void 0);

export const queryJeBestItemDetailsTab = (state: JeDetailsState, itemId?: ID) => {
  const item = itemId ? queryJeItem(state, itemId) : void 0;
  if (isNil(item)) {
    return void 0;
  }
  if (isJeItemFromComponent(item)) {
    return JeItemDetailsTab.Component;
  } else if (!isFormulaEmpty(item.formula)) {
    return JeItemDetailsTab.Formula;
  } else if (!isEmpty(item.cuttingList)) {
    return JeItemDetailsTab.CuttingList;
  } else if (!isEmpty(item.memo)) {
    return JeItemDetailsTab.Memo;
  }

  return JeItemDetailsTab.CuttingList;
};
